import { useEffect, useMemo, useRef, useState } from 'react';
import * as THREE from 'three';
import { Edges, Html } from '@react-three/drei';
import type { ThreeEvent } from '@react-three/fiber';
import type { LayoutBox } from '../utils/container-layout';
import { cn } from '@/lib/utils/cn';

/**
 * Caja de carga arrastrable dentro del contenedor.
 *
 * Conversión de coordenadas — el acomodo trabaja en centímetros con el alto en
 * Z, y three.js en metros con el alto en Y:
 *
 *   three.x = packing.x / 100      (largo)
 *   three.y = packing.z / 100      (alto)
 *   three.z = packing.y / 100      (profundidad)
 *
 * El arrastre lo coordina el editor: este componente solo reporta los eventos
 * de puntero y se dibuja según el estado que recibe.
 */

export interface DraggableCargoBoxProps {
  box: LayoutBox;
  color: string;
  label: string;
  isSelected: boolean;
  isDragging: boolean;
  /** `true` si choca con la caja que se está arrastrando — se resalta en rojo. */
  isCollisionTarget: boolean;
  /** Atenúa la caja cuando otra está seleccionada, para dar foco. */
  isDimmed: boolean;
  onSelect: (id: string) => void;
  onDragStart: (id: string, event: ThreeEvent<PointerEvent>) => void;
  /**
   * El rótulo a tamaño fijo, en píxeles. Con cámara ortográfica (la de Comercia)
   * `distanceFactor` lo escala con el zoom y terminaba tapando la escena entera con
   * un velo claro al elegir una caja.
   */
  rotuloFijo?: boolean;
  /**
   * Colores planos, sin luces: cada cara con un tono fijo del color del producto
   * (arriba más clara, el lado de la puerta más oscuro), como la maqueta de
   * Comercia. Las unidades pegadas del mismo producto se leen como un bloque.
   */
  plana?: boolean;
}

const COLLISION_COLOR = '#ef4444';

/**
 * Cuánto se aclara u oscurece cada cara, en el orden de `BoxGeometry` (+x, −x,
 * +y, −y, +z, −z). Medido sobre la maqueta: arriba ×1,12, el frente ×0,92 y el
 * lado de la puerta ×0,78 del color del producto.
 */
const TONO_POR_CARA = [0.78, 0.78, 1.12, 0.7, 0.92, 0.92];

/** El color con el brillo multiplicado en sRGB, que es como se midió en la maqueta. */
function tono(color: string, factor: number): string {
  const n = new THREE.Color(color).getHex();
  const canal = (corrimiento: number) =>
    Math.min(255, Math.round(((n >> corrimiento) & 255) * factor));
  return `rgb(${canal(16)}, ${canal(8)}, ${canal(0)})`;
}

export function DraggableCargoBox({
  box,
  color,
  label,
  isSelected,
  isDragging,
  isCollisionTarget,
  isDimmed,
  onSelect,
  onDragStart,
  rotuloFijo = false,
  plana = false,
}: DraggableCargoBoxProps) {
  const meshRef = useRef<THREE.Mesh>(null);
  const [hovered, setHovered] = useState(false);

  const l = box.lengthCm / 100;
  const w = box.widthCm / 100;
  const h = box.heightCm / 100;
  const x = box.x / 100;
  const y = box.z / 100; // alto
  const z = box.y / 100; // profundidad

  const displayColor = isCollisionTarget ? COLLISION_COLOR : color;

  /*
    Plana: el tono de cada cara va pintado en la geometría (colores por vértice)
    y no en seis materiales, para que cada caja siga siendo un solo dibujo: con
    470 bultos, seis materiales por caja eran casi tres mil.
  */
  const geometriaPlana = useMemo(() => {
    if (!plana) return null;
    const g = new THREE.BoxGeometry(l, h, w);
    const colores = new Float32Array(24 * 3);
    const c = new THREE.Color();
    TONO_POR_CARA.forEach((factor, cara) => {
      // La elegida, un poco más clara.
      c.setStyle(tono(displayColor, factor * (isSelected ? 1.12 : 1)));
      for (let v = 0; v < 4; v++) colores.set([c.r, c.g, c.b], (cara * 4 + v) * 3);
    });
    g.setAttribute('color', new THREE.BufferAttribute(colores, 3));
    return g;
  }, [plana, l, h, w, displayColor, isSelected]);
  useEffect(() => () => geometriaPlana?.dispose(), [geometriaPlana]);

  /*
    La caja va OPACA salvo cuando la translucidez dice algo.

    🔴 El default era 0,92 con `transparent`. Con seis bultos se veía bien; con
    cien —el caso real— cada caja dejaba ver las tres de atrás y el contenedor
    entero se leía como una mancha rayada. El portal del cliente las dibuja
    sólidas y por eso se entiende de un vistazo.

    La translucidez se reserva para lo que la necesita: la caja que se arrastra
    (para ver dónde va a caer) y las atenuadas cuando hay una seleccionada.
  */
  let opacity = 1;
  if (isDragging) opacity = 0.35;
  else if (isDimmed) opacity = 0.55;

  /*
    Las aristas SOLO cuando cargan información.

    🔴 Antes se dibujaban en las cien cajas, con `#00000030`. La intención era
    separar bultos del mismo color pegados, pero el resultado con la carga real
    es una maraña de líneas: se pierde justo lo que se quería mostrar. Ahora la
    separación la hace el sombreado —que con cajas opacas se ve— y las aristas
    marcan lo que hay que mirar: la caja elegida, la que se está por pisar, y la
    que está bajo el cursor.
  */
  const mostrarAristas = isSelected || isCollisionTarget || hovered;
  let edgeColor = '#e2e8f0';
  if (isCollisionTarget) edgeColor = COLLISION_COLOR;
  else if (isSelected) edgeColor = '#ffffff';

  /*
    Caja simple, no redondeada.

    🔴 Acá había un `RoundedBox` con `smoothness={4}`: genera una geometría
    redondeada propia POR CAJA. Con un producto de 200 unidades son 200
    geometrías talladas para un radio de 5 mm que a esta escala no se ve. Al
    pasar a dibujar los bultos de verdad —470 en el contenedor de prueba— eso
    era el grueso de los segundos que la pantalla se quedaba trabada.

  */
  return (
    <mesh
      ref={meshRef}
      {...(geometriaPlana ? { geometry: geometriaPlana } : {})}
      position={[x + l / 2, y + h / 2, z + w / 2]}
      castShadow
      receiveShadow
      onPointerDown={(e: ThreeEvent<PointerEvent>) => {
        e.stopPropagation();
        onSelect(box.id);
        onDragStart(box.id, e);
      }}
      onPointerOver={(e: ThreeEvent<PointerEvent>) => {
        e.stopPropagation();
        setHovered(true);
        document.body.style.cursor = 'grab';
      }}
      onPointerOut={() => {
        setHovered(false);
        document.body.style.cursor = 'auto';
      }}
    >
      {geometriaPlana ? (
        <meshBasicMaterial vertexColors opacity={opacity} transparent={opacity < 1} />
      ) : (
        <>
          <boxGeometry args={[l, h, w]} />
          <meshStandardMaterial
            color={displayColor}
            opacity={opacity}
            transparent={opacity < 1}
            // Los mismos valores que el contenedor del portal del cliente, para que
            // las dos pantallas muestren la misma carga con el mismo aspecto.
            metalness={0.08}
            roughness={0.62}
            emissive={isSelected ? displayColor : '#000000'}
            emissiveIntensity={isSelected ? 0.25 : 0}
          />
        </>
      )}
      {mostrarAristas && (
        <Edges color={edgeColor} lineWidth={isSelected || isCollisionTarget ? 2.5 : 1.5} />
      )}

      {(hovered || isSelected) && !isDragging && (
        <Html
          position={[0, h / 2 + 0.06, 0]}
          center
          distanceFactor={rotuloFijo ? undefined : 7}
          style={{ pointerEvents: 'none' }}
        >
          <div
            className={
              rotuloFijo
                ? 'bg-foreground/90 text-background px-2 py-1 rounded-md text-[11px] whitespace-nowrap select-none font-medium shadow-lg'
                : 'bg-muted/90 text-white px-2 py-1 rounded-md text-[10px] whitespace-nowrap select-none font-medium shadow-lg'
            }
          >
            {label}
            <span className={cn('ml-1.5', rotuloFijo ? 'text-background/60' : 'text-white/50')}>
              {box.lengthCm}×{box.widthCm}×{box.heightCm}
            </span>
            {box.rotated && <span className="text-alerta ml-1.5">↻</span>}
          </div>
        </Html>
      )}
    </mesh>
  );
}

/**
 * Silueta de destino: dónde va a quedar la caja al soltarla.
 *
 * Verde si la posición es válida, roja si no. Es lo que le da al arrastre la
 * sensación de Tetris — se ve el encastre antes de confirmar.
 */
export function GhostBox({ box, valid }: { box: LayoutBox; valid: boolean }) {
  const l = box.lengthCm / 100;
  const w = box.widthCm / 100;
  const h = box.heightCm / 100;
  const color = valid ? '#22c55e' : '#ef4444';

  return (
    <mesh position={[box.x / 100 + l / 2, box.z / 100 + h / 2, box.y / 100 + w / 2]}>
      <boxGeometry args={[l, h, w]} />
      <meshBasicMaterial color={color} transparent opacity={0.22} depthWrite={false} />
      <Edges color={color} lineWidth={2} />
    </mesh>
  );
}
